/**
 * Reports-surface capability for the signed-in caller.
 *
 * `view-reports` is the first slug that gates a pure READ surface, and
 * its scope is org-wide: holding it means seeing every contributor's
 * completed work across every project, not just the projects you are a
 * member of. That is the whole point of a performance dashboard — a
 * manager comparing two people cannot be shown only the half they share
 * a project with — and it is why the gate is a slug rather than project
 * membership.
 *
 * Gate on the SLUG, never on `membership.role`: an owner can edit any
 * system role's permission set in Settings → Roles, so a role-derived
 * switch goes silently wrong the moment a tenant customizes one. The
 * seeded grant is owner + admin + manager; a tenant is free to widen or
 * narrow it and this hook follows.
 *
 * Fall-open contract, identical to `tenantPermissionsFromMe` /
 * `cardPermissionsFromMe`: an ABSENT `permissions` array means an older
 * BE that doesn't serialize the field (or `useMe` still in flight), so
 * the capability reads `true` and the BE's 403 stays the backstop — a
 * FE-first deploy is a no-op rather than a lockout. A PRESENT but empty
 * array is a real answer and falls closed.
 *
 * Note the asymmetry with guests, which is deliberate: a guest is
 * REDIRECTED off `/reports` (time-and-performance surfaces are outside
 * the guest ceiling entirely and every fetch would 403), while a member
 * without the slug gets a panel explaining that an administrator can
 * grant it. A missing slug is grantable; guest-ness is not.
 */

import { useMemo } from 'react'

import type { Me } from '@/api/types-bridge'
import { useMe } from './use-me'

/** The tenant permission slug that opens the reports dashboard. */
export const VIEW_REPORTS_SLUG = 'view-reports'

export interface ReportsPermissions {
  /** May open `/reports` and read the org-wide performance aggregate. */
  canViewReports: boolean
}

export function reportsPermissionsFromMe(me: Me | undefined): ReportsPermissions {
  const slugs = me?.membership?.permissions
  if (slugs == null) return { canViewReports: true }
  return { canViewReports: slugs.includes(VIEW_REPORTS_SLUG) }
}

export function useReportsPermission(): ReportsPermissions {
  const me = useMe()
  return useMemo(() => reportsPermissionsFromMe(me.data), [me.data])
}
